import React from 'react';

type UseDynamicThresholdProps = {
  ref: React.RefObject<HTMLDivElement>;
  defaultThreshold?: number;
};

/**
 * This hook is used to calculate the threshold for the intersection observer.
 * It is used to determine when the element is visible in the viewport.
 * It is based on the default threshold and the viewport height.
 * If the viewport height is smaller than the default threshold of the element height,
 * the threshold is calculated as the viewport height / element height.
 */
export const useDynamicThreshold = ({ ref, defaultThreshold = 0.4 }: UseDynamicThresholdProps) => {
  const [threshold, setThreshold] = React.useState<number>(defaultThreshold);

  React.useEffect(() => {
    const calculateThreshold = () => {
      if (!ref.current) return;

      const elementHeight = ref.current.offsetHeight;
      const viewportHeight = window.innerHeight;
      const elementThreshold = elementHeight * defaultThreshold;

      // If viewport height is smaller than the default threshold of element height,
      // use viewport height / element height as threshold
      if (viewportHeight < elementThreshold) {
        const dynamicThreshold = Math.min(viewportHeight / elementHeight, 1.0);
        setThreshold(dynamicThreshold);
      } else {
        // Otherwise use the default threshold
        setThreshold(defaultThreshold);
      }
    };

    // Calculate threshold after component mounts and on window resize
    calculateThreshold();
    window.addEventListener('resize', calculateThreshold);

    return () => {
      window.removeEventListener('resize', calculateThreshold);
    };
  }, [ref, defaultThreshold]);

  return threshold;
};
